import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';

import { getCertPdfUrl, getReportPdfUrl } from '../../../api';
import { getLanguageCode } from '../../../lib/context';
import { downloadFromUrl } from '../../../lib/download';
import { formatUtcOffset } from '../../../lib/timezone';
import { getReportData } from '../api/downloadsApi';

const formatCertData = (body: any, timeOffset: any) => {
    if (!body?.calibration_points) {
        return null;
    }

    return {
        id: body.device_id,
        calibrationPoints: body.calibration_points,
        calibrationTime: dayjs(body.calibration_time).add(timeOffset, 'm').format('YYYY-MM-DD'),
        expirationTime: dayjs(body.expire_time).add(timeOffset, 'm').format('YYYY-MM-DD'),
        model_info: body.model_info,
    };
};

const formatReportData = (body: any, timeOffset: any) => {
    if (!body?.temps) {
        return null;
    }

    const startTime = body.start_time;
    const dataList = body.temps.map((temperature: any, index: any) => ({
        time: dayjs(startTime)
            .add(timeOffset, 'm')
            .add(index * 10, 'm')
            .format('YYYY-MM-DD HH:mm'),
        temperature,
    }));

    const now = dayjs();

    return {
        id: body.device_id,
        startTime: dayjs(body.start_time).add(timeOffset, 'm').format('YYYY-MM-DD HH:mm'),
        endTime: dayjs(body.end_time).add(timeOffset, 'm').format('YYYY-MM-DD HH:mm'),
        number_of_record: dataList.length,
        interval: body.interval,
        createTime: body.upload_time,
        shipper: body.meta_data?.shipper_name,
        is_normal: body.is_normal,
        track_number: body.meta_data?.shipment_number,
        timeZone: dayjs(now).format('UTCZ'),
        detailRowData: dataList,
        upperLimit: body.threshold?.upper_limit,
        lowerLimit: body.threshold?.lower_limit,
        timeLimit: body.threshold?.time_limit,
        thresholdType: body.threshold?.type === 'continuous' ? 'Single Event' : 'Cumulative',
        model_info: body.model_info,
    };
};

export default function useDownloadResources({ initialDeviceId = '', initialIndex = -1 }: any = {}) {
    const [deviceId, setDeviceId] = useState(initialDeviceId);
    const [deviceIndex, setDeviceIndex] = useState(Number.isFinite(initialIndex) ? initialIndex : -1);
    const [inputValue, setInputValue] = useState(initialDeviceId || '');
    const [page, setPage] = useState(initialDeviceId ? 2 : 1);
    const [isPreparing, setPreparing] = useState(false);
    const [message, setMessage] = useState('');
    const [hasError, setHasError] = useState(false);
    const [lockInput, setLockInput] = useState(false);
    const [reportData, setReportData] = useState<any>(null);
    const [certData, setCertData] = useState<any>(null);

    const { i18n } = useTranslation();
    const currentLanguage = i18n.language;

    useEffect(() => {
        let isCancelled = false;

        const fetchReportData = async () => {
            if (!deviceId) {
                return;
            }

            setMessage('Loading data...');
            setPreparing(true);
            setHasError(false);
            setReportData(null);
            setCertData(null);

            try {
                const response = await getReportData(deviceId, deviceIndex);
                if (isCancelled) {
                    return;
                }

                const body = response?.body || {};

                if (Object.keys(body).length === 0) {
                    setHasError(true);
                    setLockInput(false);
                    return;
                }

                const timeOffset = dayjs().utcOffset();
                const certPayload = formatCertData(body, timeOffset);
                const reportPayload = formatReportData(body, timeOffset);

                setCertData(certPayload);
                setReportData(reportPayload);
                setLockInput(true);
                setPage(2);
            } catch (error) {
                console.error(error);
                if (!isCancelled) {
                    setHasError(true);
                    setLockInput(false);
                }
            } finally {
                if (!isCancelled) {
                    setPreparing(false);
                }
            }
        };

        fetchReportData();

        return () => {
            isCancelled = true;
        };
    }, [deviceId, deviceIndex]);

    const handleConfirm = useCallback(() => {
        setDeviceId(inputValue);
        setDeviceIndex(-1);
    }, [inputValue]);

    const handleReset = useCallback(() => {
        setPage(1);
        setLockInput(false);
        setReportData(null);
        setCertData(null);
        setDeviceId('');
        setDeviceIndex(-1);
        setInputValue('');
        setHasError(false);
    }, []);

    const downloadReport = useCallback(async () => {
        if (!deviceId) {
            return;
        }

        setMessage(`Preparing to download Data-report-${deviceId}.pdf`);
        setPreparing(true);

        try {
            const response = await getReportPdfUrl(
                deviceId,
                0,
                formatUtcOffset(),
                getLanguageCode(currentLanguage),
                true
            );
            if (response.status === 'success' && response.url) {
                // Use start/end time if available
                let startDate = 'unknown';
                let endDate = 'unknown';
                if (reportData?.startTime) startDate = dayjs(reportData.startTime).format('YYYY-MM-DD');
                if (reportData?.endTime) endDate = dayjs(reportData.endTime).format('YYYY-MM-DD');
                const fileName = `Data-report-${deviceId}-${startDate}-to-${endDate}.pdf`;
                await downloadFromUrl(response.url, fileName);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setPreparing(false);
        }
    }, [deviceId, reportData, currentLanguage]);

    const downloadCert = useCallback(async () => {
        if (!deviceId) {
            return;
        }

        setMessage(`Preparing to download Calibration-certificate-${deviceId}.pdf`);
        setPreparing(true);

        try {
            const response = await getCertPdfUrl(deviceId, formatUtcOffset(), getLanguageCode(currentLanguage), true);
            if (response.status === 'success' && response.url) {
                await downloadFromUrl(response.url, `Calibration-certificate-${deviceId}.pdf`);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setPreparing(false);
        }
    }, [deviceId, currentLanguage]);

    const state = useMemo(
        () => ({
            inputValue,
            page,
            lockInput,
            reportReady: Boolean(reportData),
            certReady: Boolean(certData),
            reportData,
            certData,
            isPreparing,
            message,
            hasError,
        }),
        [inputValue, page, lockInput, reportData, certData, isPreparing, message, hasError]
    );

    const actions = useMemo(
        () => ({
            setInputValue,
            confirmDevice: handleConfirm,
            reset: handleReset,
            downloadReport,
            downloadCert,
        }),
        [handleConfirm, handleReset, downloadReport, downloadCert]
    );

    return { state, actions };
}
